import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import http from "../../../commonFunctions/httpClient";
import WSButton from "../../../Components/Button/WSButton";
import logo from "../../../images/logo_invoice.svg";

interface OrderSummary {
  orderNumber: string;
  patientName: string;
  orderAmount: number;
  deliveryCharge: number;
  discountApplied: number;
  serviceCharge: number;
  finalPayableAmount: number;
}

interface ProcessingDetails {
  paymentGateway: string | null;
  paymentMethod: string;
  paymentTime: string;
}

interface Reference {
  referenceType: string;
  referenceId: string | null;
}

interface Settlement {
  settlementId: string;
  settlementDate: string;
  amountSettled: number;
  status: string;
}

interface PaymentInvoiceResponse {
  success: boolean;
  message: string;
  data: {
    orderSummary: OrderSummary;
    processingDetails: ProcessingDetails;
    reference: Reference;
    settlement: Settlement;
    canDownloadInvoice: boolean;
    canReportIssue: boolean;
  };
}

interface PaymentInvoiceReportProps {
  paymentId: string;
}

export const PaymentInvoiceReport: React.FC<PaymentInvoiceReportProps> = ({ paymentId }) => {
  const [paymentData, setPaymentData] = useState<PaymentInvoiceResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 Fetching payment for paymentId:", paymentId);
      
      // ✅ CORRECT API ENDPOINT
      const response: PaymentInvoiceResponse = await http.get(
        `pharmacy/PhPayment/${paymentId}/details`
      );
      
      console.log("📊 FULL PAYMENT API RESPONSE:", response);
      
      if (response.success && response.data) {
        setPaymentData(response.data);
        console.log("✅ Payment data set:", response.data);
      } else {
        console.error("❌ API Success=false or no data:", response);
        setError(`API Error: ${response.message || 'No valid payment data'}`);
        setPaymentData(null);
      }
      
    } catch (error: any) {
      console.error("❌ PAYMENT API ERROR:", error);
      console.error("❌ Error response:", error.response?.data);
      setError(`Failed to fetch payment data: ${error.message || 'Network error'}`);
      setPaymentData(null);
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  // ... rest of component stays EXACTLY the same


  useEffect(() => {
    if (paymentId) {
      fetchPaymentData();
    }
  }, [fetchPaymentData]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading payment invoice...</Typography>
      </Box>
    );
  }

  if (error || !paymentData) {
    return (
      <Box p={4}>
        <Alert severity="error">
          {error || "No payment data found for this payment ID"}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Payment ID: {paymentId}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={4} sx={{ backgroundColor: "#fff", maxWidth: "100%" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <WSButton
          id="printPaymentInvoiceBtn"
          size="small"
          label={`Print Payment #${paymentId.slice(-6).toUpperCase()}`}
          onClick={handlePrint}
        />
      </Box>

      <Box
        ref={contentRef}
        sx={{ 
          p: 4, 
          width: "100%", 
          maxWidth: "800px", 
          margin: "0 auto",
          bgcolor: "white",
          '@media print': {
            maxWidth: "none",
            width: "100%",
            p: 2,
            margin: 0
          }
        }}
      >
        {/* HEADER */}
        <Grid container sx={{ mb: 3 }}>
          {/* LOGO - TOP CENTER */}
          <Grid item xs={12} textAlign="left" sx={{ mb: -4 }}>
            <Box
              component="img"
              src={logo}
              alt="MELP Pharmacy Logo"
              sx={{
                width: 100,
                height: 100,
                borderRadius: "12px",
                objectFit: "contain",
                '@media print': {
                  filter: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }
              }}
              onError={(e: any) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Grid>
          
          {/* BOTTOM ROW - MELP Pharmacy LEFT | EMPTY RIGHT */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={6}>
                <Typography fontSize={20} fontWeight="bold" sx={{ color: "#098963", mb: 1 }}>
                  MELP Pharmacy
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Medical Excellence Local Pharmacy
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  123 Health Street, Kerala - 670001
                </Typography>
                <Typography variant="body2">
                  Phone: +91 98765 43210
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ minHeight: 80 }} />
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* PAYMENT DETAILS */}
        <Grid container justifyContent="space-between" mb={3}>
          <Grid item xs={6}>
            <Typography fontSize={18} gutterBottom sx={{ fontWeight: 700 }}>
              Payment Invoice #{paymentId.slice(-6).toUpperCase()}
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Patient:</strong> {paymentData.orderSummary.patientName}
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Order:</strong> {paymentData.orderSummary.orderNumber}
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography fontSize={18} sx={{ fontWeight: 700, mb: 2 }}>
              <strong>Date:</strong> {formatDate(paymentData.processingDetails.paymentTime)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Method:</strong> {paymentData.processingDetails.paymentMethod}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Gateway:</strong> {paymentData.processingDetails.paymentGateway || 'N/A'}
            </Typography>
            <Chip
              label={paymentData.settlement.status}
              color={paymentData.settlement.status === "Paid" ? "success" : "default"}
              sx={{ fontWeight: 600 }}
            />
          </Grid>
        </Grid>

        {/* ORDER SUMMARY TABLE */}
        <TableContainer sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#098963" }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Order Amount</TableCell>
                <TableCell align="right">{formatCurrency(paymentData.orderSummary.orderAmount)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Delivery Charge</TableCell>
                <TableCell align="right">{formatCurrency(paymentData.orderSummary.deliveryCharge)}</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 500 }}>Discount Applied</TableCell>
                <TableCell align="right" color="error">-{formatCurrency(paymentData.orderSummary.discountApplied)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Service Charge</TableCell>
                <TableCell align="right">{formatCurrency(paymentData.orderSummary.serviceCharge)}</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#e8f5e8' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '1.1em' }}>
                  Final Payable Amount
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em', color: '#098963' }}>
                  {formatCurrency(paymentData.orderSummary.finalPayableAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* SETTLEMENT DETAILS */}
        <Box sx={{ borderTop: "3px solid #098963", pt: 3, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#098963" }}>
                💳 Payment Processing
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Method:</strong> {paymentData.processingDetails.paymentMethod}
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Gateway:</strong> {paymentData.processingDetails.paymentGateway || 'Direct'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Time:</strong> {formatDate(paymentData.processingDetails.paymentTime)}
              </Typography>
              <Typography variant="body1">
                <strong>Reference:</strong> {paymentData.reference.referenceType}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#098963" }}>
                ✅ Settlement Details
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>ID:</strong> {paymentData.settlement.settlementId.slice(-8).toUpperCase()}
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Date:</strong> {formatDate(paymentData.settlement.settlementDate)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Amount:</strong> {formatCurrency(paymentData.settlement.amountSettled)}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#098963' }}>
                <strong>Status:</strong> {paymentData.settlement.status}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* FOOTER */}
        <Divider sx={{ my: 4 }} />
        <Box textAlign="center">
          <Typography variant="h5" color="#098963" sx={{ fontWeight: 700, mb: 2 }}>
            💊 Get well soon 💙
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Thank you for choosing MELP Pharmacy | Kerala
          </Typography>
          <Typography sx={{ 
            fontSize: '0.8rem', 
            color: 'text.secondary', 
            mt: 2,
            '@media print': { fontSize: '0.9rem' }
          }}>
            * This is a computer-generated payment invoice
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
